import React, { lazy, Suspense } from 'react';
import { Route, Routes, useLocation, Navigate, useParams, generatePath } from 'react-router-dom';

import {
  buildPulseQuestionProfilePath,
  PULSE_DRIVERS_SECTION_PATH,
} from '@/views/pulse/core/pulseProfileNavigation';
import { ComplianceModuleGate } from '@/views/compliance/ComplianceModuleGate';
import {
  ComplianceIndexRedirect,
  CompliancePolicyLegacyRedirect,
} from '@/views/compliance/ComplianceLegacyRedirects';
import { CustomerSatisfactionModuleGate } from '@/views/customerSatisfaction/CustomerSatisfactionModuleGate';
import { CustomerSatisfactionIndexRedirect } from '@/views/customerSatisfaction/CustomerSatisfactionIndexRedirect';
import { CompanyValuesSetupGate } from '@/views/PLG/CompanyValuesSetupGate';

/** @returns {JSX.Element} */
function PulseQuestionProfileLegacyRedirect() {
  const { questionID } = useParams();
  return <Navigate to={buildPulseQuestionProfilePath(questionID)} replace />;
}

/** @returns {JSX.Element} */
function PulseDriverProfileLegacyRedirect() {
  const { encryptedParams } = useParams();
  return <Navigate to={`${PULSE_DRIVERS_SECTION_PATH}/${encryptedParams}`} replace />;
}

/** @returns {JSX.Element} */
function AdminV2CompanyDetailRedirect() {
  const { companyId } = useParams();
  return (
    <Navigate
      to={generatePath('/admin/companies/:companyId', { companyId })}
      replace
    />
  );
}

/** @returns {JSX.Element} */
function AdminV2CampaignDetailRedirect() {
  const { campaignId } = useParams();
  return (
    <Navigate
      to={generatePath('/admin/campaigns/:campaignId', { campaignId })}
      replace
    />
  );
}

import Configs from '../Configs';
import {
  HRBP_MODULE_CODE_CALIBRATION,
  HRBP_MODULE_CODE_DEVELOPMENT,
  HRBP_MODULE_CODE_ONBOARDING,
  HRBP_MODULE_CODE_ONEON1S,
  HRBP_MODULE_CODE_PERFORMANCE,
  HRBP_MODULE_CODE_PULSE,
  HRBP_MODULE_CODE_RECOGNITION,
} from '@/constants/hrbpRouteModuleCodes';

import ErrorBoundary from '../ErrorBoundary';

import ProtectedRoute from './ProtectedRoute'; // adjust path

// Layout
const Layout = lazy(() => import('../layouts/Layout'));
const LayoutHeaderless = lazy(() => import('../layouts/LayoutHeaderless'));

// PLG
const SlackOnboarding = lazy(() => import('../views/PLG/slackOnboarding'));
const NewCompanyOnboarding = lazy(() =>
  import('../views/PLG/NewCompanyOnboarding').then((m) => ({
    default: m.NewCompanyOnboarding,
  }))
);
const CompanyValuesSetup = lazy(() =>
  import('../views/PLG/CompanyValuesSetup').then((m) => ({
    default: m.CompanyValuesSetup,
  }))
);
const GettingStarted = lazy(() =>
  import('../views/PLG/GettingStarted').then((m) => ({
    default: m.GettingStarted,
  }))
);

// DEVELOP
const RecruitmentInterviews = lazy(() => import('../views/recruitment/recruitmentInterviews'));
const RecruitmentCandidates = lazy(() =>
  import('../views/recruitment/RecruitmentCandidates').then((m) => ({
    default: m.RecruitmentCandidates,
  }))
);
const RecruitmentCandidateCreate = lazy(() =>
  import('../views/recruitment/RecruitmentCandidateCreate').then((m) => ({
    default: m.RecruitmentCandidateCreate,
  }))
);
const RecruitmentJobs = lazy(() =>
  import('../views/recruitment/RecruitmentJobs').then((m) => ({
    default: m.RecruitmentJobs,
  }))
);
const RecruitmentJobCreate = lazy(() =>
  import('../views/recruitment/RecruitmentJobCreate').then((m) => ({
    default: m.RecruitmentJobCreate,
  }))
);
const RecruitmentJobDetail = lazy(() =>
  import('../views/recruitment/RecruitmentJobDetail').then((m) => ({
    default: m.RecruitmentJobDetail,
  }))
);
const RecruitmentReports = lazy(() => import('../views/recruitment/recruitmentReports'));
const RecruitmentCareersSettings = lazy(() =>
  import('../views/recruitment/RecruitmentCareersSettings').then((m) => ({
    default: m.RecruitmentCareersSettings,
  }))
);

/** Interviewer+ — shared gate for Recruitment module routes (tabs filtered further in header). */
const RECRUITMENT_ROUTE_ROLES = [
  Configs.securityLevelInterviewer,
  Configs.securityLevelManager,
  Configs.securityLevelHR,
  Configs.securityLevelExec,
  Configs.securityLevelAdmin,
];

const OnboardingHR = lazy(() => import('../views/onboarding/onboardingHR'));
const OnboardingPlan = lazy(() => import('../views/onboarding/onboardingPlan'));
const OnboardingTemplate = lazy(() => import('../views/onboarding/onboardingTemplate'));
const OnboardingTemplates = lazy(() => import('../views/onboarding/onboardingTemplates'));
const OnboardingReporting = lazy(() =>
  import('../views/onboarding/onboardingReporting').then((m) => ({
    default: m.OnboardingReporting,
  }))
);

const CareerHome = lazy(() =>
  import('../views/career/careerHome.tsx').then((m) => ({ default: m.CareerHome }))
);
const CareerTeam = lazy(() =>
  import('../views/career/careerTeam.tsx').then((m) => ({ default: m.CareerTeam }))
);
const CareerHR = lazy(() =>
  import('../views/career/careerHR.tsx').then((m) => ({ default: m.CareerHR }))
);
const CareerReporting = lazy(() =>
  import('../views/career/careerReporting.tsx').then((m) => ({ default: m.CareerReporting }))
);
const CareerGuidelineCreate = lazy(() =>
  import('../views/career/careerGuidelineCreate.tsx').then((m) => ({
    default: m.CareerGuidelineCreate,
  }))
);

const LearningHome = lazy(() => import('../views/learning/learningHome'));
const LearningHomeTeamMember = lazy(() => import('../views/learning/learningHomeTeamMember'));
const LearningDashboardTeamRedirect = lazy(
  () => import('../views/learning/LearningDashboardTeamRedirect')
);
const LearningTeam = lazy(() => import('../views/learning/learningTeam'));
const LearningCatalogue = lazy(() => import('../views/learning/learningCatalogue'));
const LearningCourseProfile = lazy(() => import('../views/learning/learningCourseProfile'));

// PERFORM
const PerformanceHome = lazy(() => import('../views/performance/performanceHome'));
const PerformanceCampaignPeers = lazy(
  () => import('../views/performance/performanceCampaignPeers')
);
const PerformanceInput = lazy(() => import('../views/performance/performanceInput'));
const PerformanceReview = lazy(() => import('../views/performance/performanceReview'));
const PerformanceDiscuss = lazy(() => import('../views/performance/performanceDiscuss'));
const PerformanceFeedback = lazy(() => import('../views/performance/performanceFeedback'));
const PerformanceFeedbacks = lazy(() => import('../views/performance/performanceFeedbacks'));

const PerformanceCampaignResult = lazy(
  () => import('../views/performance/performanceCampaignResult')
);

const PerformanceTeam = lazy(() =>
  import('../views/performance/PerformanceTeam').then((m) => ({ default: m.PerformanceTeam }))
);

const PerformanceHR = lazy(() => import('../views/performance/performanceHR'));
const PerformanceCampaigns = lazy(() => import('../views/performance/performanceCampaigns'));
const PerformanceCampaign = lazy(() => import('../views/performance/performanceCampaign'));
const PerformanceAdmin = lazy(() => import('../views/performance/performanceAdmin'));

const CalibrationHome = lazy(() =>
  import('../views/calibration/calibrationHome').then((m) => ({ default: m.CalibrationHome }))
);
const CalibrationCalibrate = lazy(() =>
  import('../views/calibration/calibrationCalibrate').then((m) => ({
    default: m.CalibrationCalibrate,
  }))
);
const CalibrationSession = lazy(() =>
  import('../views/calibration/calibrationSession').then((m) => ({ default: m.CalibrationSession }))
);
const CalibrationCampaigns = lazy(() =>
  import('../views/calibration/calibrationCampaigns').then((m) => ({
    default: m.CalibrationCampaigns,
  }))
);

const GoalsHome = lazy(() =>
  import('../views/goals/GoalsHome').then((m) => ({ default: m.GoalsHome }))
);
const GoalsCompany = lazy(() =>
  import('../views/goals/GoalsCompany').then((m) => ({ default: m.GoalsCompany }))
);
const GoalsTeam = lazy(() =>
  import('../views/goals/GoalsTeam').then((m) => ({ default: m.GoalsTeam }))
);
const GoalsTeamMember = lazy(() =>
  import('../views/goals/GoalsTeamMember').then((m) => ({ default: m.GoalsTeamMember }))
);
const GoalCreate = lazy(() =>
  import('@/views/goals/GoalCreate').then((m) => ({ default: m.GoalCreate }))
);
const GoalProfile = lazy(() =>
  import('../views/goals/GoalProfile').then((m) => ({ default: m.GoalProfile }))
);

const DevelopmentHomeSelf = lazy(() =>
  import('../views/development/developmentHomeSelf').then((m) => ({
    default: m.DevelopmentHomeSelf,
  }))
);
const DevelopmentHomeShared = lazy(() =>
  import('../views/development/developmentHomeShared').then((m) => ({
    default: m.DevelopmentHomeShared,
  }))
);
const DevelopmentHomeTeamMember = lazy(() =>
  import('../views/development/developmentHomeTeamMember').then((m) => ({
    default: m.DevelopmentHomeTeamMember,
  }))
);
const DevelopmentTeam = lazy(() =>
  import('../views/development/developmentTeam').then((m) => ({ default: m.DevelopmentTeam }))
);
const DevelopmentPlanCreate = lazy(() =>
  import('../views/development/developmentPlanCreate').then((m) => ({
    default: m.DevelopmentPlanCreate,
  }))
);
const DevelopmentPlanProfile = lazy(() =>
  import('../views/development/developmentPlanProfile').then((m) => ({
    default: m.DevelopmentPlanProfile,
  }))
);
const DevelopmentDashboard = lazy(() =>
  import('../views/development/developmentDashboard').then((m) => ({
    default: m.DevelopmentDashboard,
  }))
);
const DevelopmentPlanTemplate = lazy(() =>
  import('../views/development/developmentPlanTemplate').then((m) => ({
    default: m.DevelopmentPlanTemplate,
  }))
);
const DevelopmentTemplates = lazy(() =>
  import('../views/development/developmentTemplates').then((m) => ({
    default: m.DevelopmentTemplates,
  }))
);
// const DevelopmentSettings = lazy(() => import('../views/development/developmentSettings.jsx'));

// ENGAGE
const PulseHome = lazy(() => import('../views/pulse/pulseHome'));
const PulseQuestionProfile = lazy(() => import('../views/pulse/pulseQuestionProfile'));
const PulseDriverProfile = lazy(() => import('../views/pulse/pulseDriverProfile'));
const PulseCreateCampaign = lazy(() => import('../views/pulse/pulseCreateCampaign'));
const PulseCampaigns = lazy(() => import('../views/pulse/pulseCampaigns'));
const PulseCampaignProfile = lazy(() => import('../views/pulse/pulseCampaignProfile'));
const PulseCampaignSummary = lazy(() =>
  import('../views/pulse/PulseCampaignSummary').then((module) => ({
    default: module.PulseCampaignSummary,
  }))
);
const PulseDriversReporting = lazy(() => import('../views/pulse/pulseDriversReporting'));
const PulseQuestionsReporting = lazy(() =>
  import('../views/pulse/pulseQuestionsReporting').then((module) => ({
    default: module.PulseQuestionsReporting,
  }))
);
const PulseCommentsReporting = lazy(() =>
  import('../views/pulse/pulseCommentsReporting').then((module) => ({
    default: module.PulseCommentsReporting,
  }))
);
const PulseResponseRateReporting = lazy(() => import('../views/pulse/pulseResponseRateReporting'));
const PulseEngagement = lazy(() => import('../views/pulse/pulseEngagement'));
const PulseComparisonHub = lazy(() =>
  import('../views/pulse/comparison/PulseComparisonHub').then((module) => ({
    default: module.PulseComparisonHub,
  }))
);
const PulseSurvey = lazy(() => import('../views/survey/pulseSurvey'));
const AuthenticatedPulseSurvey = lazy(() =>
  import('../views/survey/AuthenticatedPulseSurvey').then((module) => ({
    default: module.AuthenticatedPulseSurvey,
  }))
);
const PulseDaily = lazy(() => import('../views/survey/pulseDaily'));

const RecognitionHome = lazy(() =>
  import('../views/recognition/recognitionHome').then((m) => ({ default: m.RecognitionHome }))
);
const RecognitionCreate = lazy(() =>
  import('../views/recognition/recognitionCreate').then((module) => ({
    default: module.RecognitionCreate,
  }))
);
const RecognitionLeaderboards = lazy(() => import('../views/recognition/recognitionLeaderboards'));
const RecognitionReporting = lazy(() => import('../views/recognition/recognitionReporting'));
const RecognitionRewards = lazy(() => import('../views/recognition/recognitionRewards'));
const NewsFeed = lazy(() => import('@/views/news/NewsFeed').then((m) => ({ default: m.NewsFeed })));
const NewsDetail = lazy(() => import('@/views/news/NewsDetail').then((m) => ({ default: m.NewsDetail })));
const NewsComms = lazy(() => import('@/views/news/NewsComms').then((m) => ({ default: m.NewsComms })));
const NewsCreateEdit = lazy(() =>
  import('@/views/news/NewsCreateEdit').then((m) => ({ default: m.NewsCreateEdit }))
);
const ComplianceHome = lazy(() =>
  import('@/views/compliance/ComplianceHome').then((module) => ({
    default: module.ComplianceHome,
  }))
);
const CompliancePolicyEditor = lazy(() =>
  import('@/views/compliance/CompliancePolicyEditor').then((module) => ({
    default: module.CompliancePolicyEditor,
  }))
);

const CustomerSatisfactionDashboard = lazy(() =>
  import('@/views/customerSatisfaction/CustomerSatisfactionDashboard').then((module) => ({
    default: module.CustomerSatisfactionDashboard,
  }))
);
const CustomerSatisfactionSurveys = lazy(() =>
  import('@/views/customerSatisfaction/CustomerSatisfactionSurveys').then((module) => ({
    default: module.CustomerSatisfactionSurveys,
  }))
);
const CustomerSatisfactionCreateCampaign = lazy(() =>
  import('@/views/customerSatisfaction/CustomerSatisfactionCreateCampaign').then((module) => ({
    default: module.CustomerSatisfactionCreateCampaign,
  }))
);
const CustomerSatisfactionCampaignResults = lazy(() =>
  import('@/views/customerSatisfaction/CustomerSatisfactionCampaignResults').then((module) => ({
    default: module.CustomerSatisfactionCampaignResults,
  }))
);
const CustomerSatisfactionNpsTake = lazy(() =>
  import('@/views/customerSatisfaction/CustomerSatisfactionNpsTake').then((module) => ({
    default: module.CustomerSatisfactionNpsTake,
  }))
);
const CustomerSatisfactionNpsEmbed = lazy(() =>
  import('@/views/customerSatisfaction/CustomerSatisfactionNpsEmbed').then((module) => ({
    default: module.CustomerSatisfactionNpsEmbed,
  }))
);

const OneOnOneTeam = lazy(() =>
  import('../views/oneOnOne/oneOnOneTeam').then((module) => ({ default: module.OneOnOneTeam }))
);
const OneOnOneMeeting = lazy(() => import('../views/oneOnOne/oneOnOneMeeting'));
const OneOnOnePersonal = lazy(() => import('../views/oneOnOne/oneOnOnePersonal'));
const OneOnOneHR = lazy(() => import('../views/oneOnOne/oneOnOneHR'));

// DAYS OFF
import { DAYS_OFF_CONFIG } from '@/views/daysOff/dayOffConfig';
const DaysOffHome = lazy(() =>
  import('@/views/daysOff/daysoffHome').then((m) => ({ default: m.DaysOffHome }))
);
const DaysOffRequest = lazy(() => import('@/views/daysOff/daysoffRequest'));
const DaysOffTeam = lazy(() =>
  import('@/views/daysOff/daysoffTeam').then((m) => ({ default: m.DaysOffTeam }))
);
const DaysOffAward = lazy(() => import('@/views/daysOff/daysOffAward'));
const DaysOffTeamCalendar = lazy(() =>
  import('@/views/daysOff/daysoffTeamCalendar').then((m) => ({ default: m.DaysOffTeamCalendar }))
);
const DaysOffHR = lazy(() => import('@/views/daysOff/daysoffHR'));

// PUNCH ** LEGACY
// PUNCH V2
import { PUNCH_V2_CONFIG } from '@/views/punchv2/punchV2Config';
import { EMPLOYEE_SETTINGS_CONFIG } from '@/views/settings/employeeSettings/employeeSettingsConfig';
import {
  SETTINGS_COMPANY_SECURITY_LEVELS,
  SETTINGS_PERSONAL_SECURITY_LEVELS,
  SETTINGS_ROUTES,
} from '@/views/settings/core/settingsRoutes.ts';
const PunchV2Provider = lazy(() => import('@/views/punchv2/core/PunchV2Context'));
const PunchV2Home = lazy(() => import('@/views/punchv2/PuchV2Home'));
const PunchV2HR = lazy(() => import('@/views/punchv2/PuchV2HR'));
const EmployeeSettingsHub = lazy(() =>
  import('@/views/settings/employeeSettings/EmployeeSettingsHub.jsx').then((module) => ({
    default: module.EmployeeSettingsHub,
  }))
);
const EmployeeSettingsTabAgreements = lazy(
  () => import('@/views/settings/employeeSettings/EmployeeSettingsTabAgreements.jsx')
);
const EmployeeSettingsTabContracts = lazy(
  () => import('@/views/settings/employeeSettings/EmployeeSettingsTabContracts.jsx')
);
const EmployeeSettingsTabPending = lazy(
  () => import('@/views/settings/employeeSettings/EmployeeSettingsTabPending.jsx')
);
const EmployeeSettingsTabProjects = lazy(
  () => import('@/views/settings/employeeSettings/EmployeeSettingsTabProjects.jsx')
);
const SettingsCompanyPunch = lazy(() =>
  import('@/views/settings/punchSettings/PunchSettings').then((module) => ({
    default: module.PunchSettings,
  }))
);

import { ADMIN_CONFIG } from '@/views/admin/adminConfig';
import { AGENTS_ADMIN_CONFIG } from '@/views/admin/agentsConfig';
const AdminCompaniesList = lazy(() => import('@/views/admin/AdminCompaniesList.jsx'));
const AdminCompanyCreate = lazy(() => import('@/views/admin/AdminCompanyCreate.jsx'));
const AdminCompanyDetail = lazy(() => import('@/views/admin/AdminCompanyDetail.jsx'));
const AdminCampaignsList = lazy(() =>
  import('@/views/admin/AdminCampaignsList').then((module) => ({
    default: module.AdminCampaignsList,
  }))
);
const AdminCampaignCreate = lazy(() =>
  import('@/views/admin/AdminCampaignCreate').then((module) => ({
    default: module.AdminCampaignCreate,
  }))
);
const AdminCampaignDetail = lazy(() =>
  import('@/views/admin/AdminCampaignDetail').then((module) => ({
    default: module.AdminCampaignDetail,
  }))
);
const AgentsRoiPage = lazy(() =>
  import('@/views/admin/AgentsRoiPage').then((module) => ({
    default: module.AgentsRoiPage,
  }))
);
const AgentsPromptLibraryPage = lazy(() =>
  import('@/views/admin/AgentsPromptLibraryPage').then((module) => ({
    default: module.AgentsPromptLibraryPage,
  }))
);

const Profile = lazy(() => import('../views/profile/profile'));
const ProfileJob = lazy(() => import('../views/profile/profileJob'));
const ProfileComp = lazy(() => import('../views/profile/profileComp'));
const ProfileDocuments = lazy(() =>
  import('@/views/profile/profileDocuments').then((module) => ({
    default: module.ProfileDocuments,
  }))
);

const SettingsPersonal = lazy(() =>
  import('@/views/settings/SettingsPersonal').then((module) => ({
    default: module.SettingsPersonal,
  }))
);
const SettingsCompanyHub = lazy(() =>
  import('@/views/settings/SettingsCompanyHub').then((module) => ({
    default: module.SettingsCompanyHub,
  }))
);
const SettingsCompanyPersonalization = lazy(() =>
  import('@/views/settings/company/SettingsCompanyPersonalization').then((module) => ({
    default: module.SettingsCompanyPersonalization,
  }))
);
const SettingsCompanyIntegrations = lazy(() =>
  import('@/views/settings/company/SettingsCompanyIntegrations').then((module) => ({
    default: module.SettingsCompanyIntegrations,
  }))
);
const SettingsCompanyBilling = lazy(() =>
  import('@/views/settings/company/SettingsCompanyBilling').then((module) => ({
    default: module.SettingsCompanyBilling,
  }))
);
const SettingsCompanyPulse = lazy(() =>
  import('@/views/settings/company/SettingsCompanyPulse').then((module) => ({
    default: module.SettingsCompanyPulse,
  }))
);
const SettingsCompanyCalibration = lazy(() =>
  import('@/views/settings/company/SettingsCompanyCalibration').then((module) => ({
    default: module.SettingsCompanyCalibration,
  }))
);
const SettingsCompanyRecognition = lazy(() =>
  import('@/views/settings/company/SettingsCompanyRecognition').then((module) => ({
    default: module.SettingsCompanyRecognition,
  }))
);
const SettingsCompanyGoals = lazy(() =>
  import('@/views/settings/company/SettingsCompanyGoals').then((module) => ({
    default: module.SettingsCompanyGoals,
  }))
);
const SettingsCompanyCompliance = lazy(() =>
  import('@/views/settings/company/SettingsCompanyCompliance').then((module) => ({
    default: module.SettingsCompanyCompliance,
  }))
);
const SettingsCompanyModuleOverview = lazy(() =>
  import('@/views/settings/company/SettingsCompanyModuleOverview').then((module) => ({
    default: module.SettingsCompanyModuleOverview,
  }))
);
const SettingsIndexRedirect = lazy(() =>
  import('@/views/settings/SettingsIndexRedirect').then((module) => ({
    default: module.SettingsIndexRedirect,
  }))
);
const SettingsKAI = lazy(() => import('../views/settings/settingsKAI'));
const SettingsCompanyHrPocs = lazy(() =>
  import('@/views/settings/company/SettingsCompanyHrPocs').then((module) => ({
    default: module.SettingsCompanyHrPocs,
  }))
);

const HRInbox = lazy(() => import('../views/HRinbox/HRinbox'));
const HRNewRequest = lazy(() => import('../views/HRinbox/HRNewRequest'));
const HRDashboard = lazy(() => import('../views/HRinbox/HRDashboard'));
const HRConversation = lazy(() => import('../views/HRinbox/HRConversation'));

const Organigram = lazy(() => import('../views/organigram/organigramHome.tsx'));
const OrganigramEmployeeListRoute = lazy(() =>
  import('../views/organigram/OrganigramEmployeeListRoute.tsx').then((m) => ({
    default: m.OrganigramEmployeeListRoute,
  }))
);
const OrganigramEmployeeCreate = lazy(
  () => import('../views/organigram/organigramEmployeeCreate.tsx')
);
const OrganigramDataModel = lazy(
  () => import('../views/organigram/organigramEmployeeDataModel.tsx')
);
const OrganigramReOrganize = lazy(() => import('../views/organigram/organigramReorganize.tsx'));
const OrganigramRolesAssignmentPage = lazy(() =>
  import('../views/organigram/OrganigramRolesAssignmentPage.tsx').then((m) => ({
    default: m.OrganigramRolesAssignmentPage,
  }))
);

// Culture
const CultureHome = lazy(() => import('../views/culture/CultureHome'));
const CultureValues = lazy(() => import('../views/culture/CultureValues'));
const CultureBehaviours = lazy(() => import('../views/culture/CultureValues'));
const CulturePerformance = lazy(() => import('../views/culture/CulturePerformance'));
const CultureDrivers = lazy(() => import('../views/culture/CultureDrivers'));
const CultureCalendar = lazy(() =>
  import('@/views/culture/calendar/CultureCalendar').then((module) => ({
    default: module.CultureCalendar,
  }))
);

// Move outside
// const ResetPassword = lazy(() => import('../authentication/resetPassword'));
const RecognitionPublicSingleRecognition = lazy(
  () => import('../views/recognition/recognitionPublicSingleRecognition')
);

// Public growth-loop pages
const TrustCenterPublic = lazy(() =>
  import('../views/trustCenter/TrustCenterPublic').then((m) => ({ default: m.TrustCenterPublic }))
);
const CareersPublic = lazy(() =>
  import('../views/careers/CareersPublic').then((m) => ({ default: m.CareersPublic }))
);

const Home = lazy(() => import('../Home').then((m) => ({ default: m.Home })));
const Loader = lazy(() => import('./support/loader'));

const Forbidden = lazy(() => import('./forbidden'));

function ComplianceHomeRoute() {
  return (
    <ComplianceModuleGate>
      <ComplianceHome />
    </ComplianceModuleGate>
  );
}

function CompliancePolicyRoute() {
  return (
    <ComplianceModuleGate>
      <CompliancePolicyEditor />
    </ComplianceModuleGate>
  );
}

function CustomerSatisfactionDashboardRoute() {
  return (
    <CustomerSatisfactionModuleGate>
      <CustomerSatisfactionDashboard />
    </CustomerSatisfactionModuleGate>
  );
}

function CustomerSatisfactionSurveysRoute() {
  return (
    <CustomerSatisfactionModuleGate>
      <CustomerSatisfactionSurveys />
    </CustomerSatisfactionModuleGate>
  );
}

function CustomerSatisfactionCreateRoute() {
  return (
    <CustomerSatisfactionModuleGate>
      <CustomerSatisfactionCreateCampaign />
    </CustomerSatisfactionModuleGate>
  );
}

function CustomerSatisfactionResultsRoute() {
  return (
    <CustomerSatisfactionModuleGate>
      <CustomerSatisfactionCampaignResults />
    </CustomerSatisfactionModuleGate>
  );
}

function CustomerSatisfactionTakeRoute() {
  return (
    <CustomerSatisfactionModuleGate>
      <CustomerSatisfactionNpsTake />
    </CustomerSatisfactionModuleGate>
  );
}

function CustomerSatisfactionEmbedRoute() {
  return (
    <CustomerSatisfactionModuleGate>
      <CustomerSatisfactionNpsEmbed />
    </CustomerSatisfactionModuleGate>
  );
}

function ProfileDocumentsRoute() {
  // Employee reader is not gated on activeModules.Compliance (HR Compliance UI still is).
  return <ProfileDocuments />;
}

// ---------------------------------------------------------------------------------------------------
function Routing() {
  const location = useLocation();
  const background = location.state && location.state.background;
  /**
   * @param {number[]} roles
   * @param {React.ComponentType<any>} Component
   * @param {{ hrbpModuleCode?: string | null }} [routeOptions]
   */
  const requireRole = (roles, Component, routeOptions = {}) => (
    <ProtectedRoute allowedRoles={roles} hrbpModuleCode={routeOptions.hrbpModuleCode ?? null}>
      <Component />
    </ProtectedRoute>
  );

  return (
    <React.Fragment>
      {/* Pass location to Routes so that Page remains background when ModalVersion is opened */}
      <Suspense fallback={<Loader />}>
        <ErrorBoundary>
          <Routes location={background || location}>
            {/* Product Led Growth screens - UNPROTECTED */}
            <Route path="/slack/redirect" element={<SlackOnboarding />} />
            <Route path="/customerOnboarding/" element={<NewCompanyOnboarding />} />
            <Route path="/trust-center/:publicId" element={<TrustCenterPublic />} />
            <Route path="/careers/:publicId" element={<CareersPublic />} />

            {/* Platform Layout - PROTECTED ROUTES */}
            <Route
              element={
                <CompanyValuesSetupGate>
                  <Layout />
                </CompanyValuesSetupGate>
              }
            >
              <Route
                path="/setup/company-values"
                element={requireRole([Configs.securityLevelIC], CompanyValuesSetup)}
              />
              <Route
                path="/getting-started/:campaignSlug"
                element={requireRole([Configs.securityLevelIC], GettingStarted)}
              />
              {/* Home */}
              <Route path="/" element={requireRole([Configs.securityLevelIC], Home)} />

              {/* ENGAGE */}
              <Route path="/recognition">
                <Route index element={requireRole([Configs.securityLevelIC], RecognitionHome)} />
                <Route
                  path="wall/"
                  element={requireRole([Configs.securityLevelIC], RecognitionHome)}
                />
                <Route
                  path="leaderboard/"
                  element={requireRole([Configs.securityLevelIC], RecognitionLeaderboards)}
                />
                <Route
                  path="reporting/"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    RecognitionReporting,
                    { hrbpModuleCode: HRBP_MODULE_CODE_RECOGNITION }
                  )}
                />
                <Route
                  path="create"
                  element={requireRole([Configs.securityLevelIC], RecognitionCreate)}
                />
                <Route
                  path="rewards/"
                  element={requireRole([Configs.securityLevelIC], RecognitionRewards)}
                />
              </Route>

              <Route path="/news">
                <Route index element={<Navigate to="/news/feed" replace />} />
                <Route path="feed/:newsId" element={requireRole([Configs.securityLevelIC], NewsDetail)} />
                <Route path="feed" element={requireRole([Configs.securityLevelIC], NewsFeed)} />
                <Route path="comms/create" element={requireRole([Configs.securityLevelHR, Configs.securityLevelExec], NewsCreateEdit)} />
                <Route path="comms/:newsId/edit" element={requireRole([Configs.securityLevelHR, Configs.securityLevelExec], NewsCreateEdit)} />
                <Route path="comms/:newsId" element={requireRole([Configs.securityLevelHR, Configs.securityLevelExec], NewsDetail)} />
                <Route path="comms" element={requireRole([Configs.securityLevelHR, Configs.securityLevelExec], NewsComms)} />
              </Route>

              <Route path="/compliance">
                <Route index element={<ComplianceIndexRedirect />} />
                <Route
                  path="dashboard"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    ComplianceHomeRoute
                  )}
                />
                <Route
                  path="dashboard/policy"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CompliancePolicyRoute
                  )}
                />
                <Route path="policy" element={<CompliancePolicyLegacyRedirect />} />
              </Route>

              <Route path="/customer-satisfaction">
                <Route index element={<CustomerSatisfactionIndexRedirect />} />
                <Route
                  path="dashboard"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CustomerSatisfactionDashboardRoute
                  )}
                />
                <Route
                  path="surveys"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CustomerSatisfactionSurveysRoute
                  )}
                />
                <Route
                  path="campaigns/create"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CustomerSatisfactionCreateRoute
                  )}
                />
                <Route
                  path="campaigns/edit/:campaignId"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CustomerSatisfactionCreateRoute
                  )}
                />
                <Route
                  path="campaigns/:campaignId/embed"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CustomerSatisfactionEmbedRoute
                  )}
                />
                <Route
                  path="campaigns/:campaignId/take"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CustomerSatisfactionTakeRoute
                  )}
                />
                <Route
                  path="campaigns/:campaignId"
                  element={requireRole(
                    [
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelSuperAdmin,
                    ],
                    CustomerSatisfactionResultsRoute
                  )}
                />
              </Route>

              <Route path="/oneonone/">
                <Route index element={<Navigate to="team" replace />} />
                <Route path="team">
                  <Route
                    index
                    element={requireRole(
                      [
                        Configs.securityLevelIC,
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      OneOnOneTeam,
                      { hrbpModuleCode: HRBP_MODULE_CODE_ONEON1S }
                    )}
                  />
                  <Route
                    path="space/:encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelIC,
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      OneOnOnePersonal,
                      { hrbpModuleCode: HRBP_MODULE_CODE_ONEON1S }
                    )}
                  />
                </Route>
                <Route
                  path="self"
                  element={requireRole(
                    [
                      Configs.securityLevelIC,
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    OneOnOnePersonal,
                    { hrbpModuleCode: HRBP_MODULE_CODE_ONEON1S }
                  )}
                />
                <Route
                  path="meeting/:encryptedParams"
                  element={requireRole(
                    [
                      Configs.securityLevelIC,
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    OneOnOneMeeting,
                    { hrbpModuleCode: HRBP_MODULE_CODE_ONEON1S }
                  )}
                />
                <Route
                  path="space/"
                  element={requireRole(
                    [
                      Configs.securityLevelIC,
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    OneOnOnePersonal,
                    { hrbpModuleCode: HRBP_MODULE_CODE_ONEON1S }
                  )}
                />
                <Route
                  path="space/:encryptedParams"
                  element={requireRole(
                    [
                      Configs.securityLevelIC,
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    OneOnOnePersonal,
                    { hrbpModuleCode: HRBP_MODULE_CODE_ONEON1S }
                  )}
                />
                <Route
                  path="hr"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    OneOnOneHR
                  )}
                />
              </Route>

              <Route path="/pulse/">
                <Route index element={<Navigate to="/pulse/dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    PulseHome,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="campaigns"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    PulseCampaigns
                  )}
                />
                <Route
                  path="campaigns/create"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    PulseCreateCampaign
                  )}
                />
                <Route
                  path="campaigns/edit/:campaignID"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    PulseCreateCampaign
                  )}
                />
                <Route
                  path="campaigns/:campaignID/summary"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    PulseCampaignSummary
                  )}
                />
                <Route
                  path="campaigns/:campaignID"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    PulseCampaignProfile
                  )}
                />
                <Route
                  path="drivers"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    PulseDriversReporting,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="questions"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    PulseQuestionsReporting,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="comments"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    PulseCommentsReporting,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="responseRate"
                  element={requireRole(
                    [Configs.securityLevelHRBP, Configs.securityLevelHR, Configs.securityLevelExec],
                    PulseResponseRateReporting,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="engagement"
                  element={requireRole(
                    [Configs.securityLevelHRBP, Configs.securityLevelHR, Configs.securityLevelExec],
                    PulseEngagement,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="comparison"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    PulseComparisonHub,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="questions/:questionID"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    PulseQuestionProfile,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route
                  path="drivers/:id"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHRBP,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    PulseDriverProfile,
                    { hrbpModuleCode: HRBP_MODULE_CODE_PULSE }
                  )}
                />
                <Route path="question/:questionID" element={<PulseQuestionProfileLegacyRedirect />} />
                <Route path="driver/:encryptedParams" element={<PulseDriverProfileLegacyRedirect />} />
              </Route>

              {/* DEVELOP — Recruitment (Layout ModuleHeader owns tabs; index → jobs) */}
              <Route path="/recruitment">
                <Route index element={<Navigate to="/recruitment/jobs" replace />} />
                <Route path="jobs">
                  <Route
                    index
                    element={requireRole(RECRUITMENT_ROUTE_ROLES, RecruitmentJobs)}
                  />
                  <Route
                    path="create"
                    element={requireRole(RECRUITMENT_ROUTE_ROLES, RecruitmentJobCreate)}
                  />
                  <Route
                    path=":jobID"
                    element={requireRole(RECRUITMENT_ROUTE_ROLES, RecruitmentJobDetail)}
                  />
                </Route>
                <Route
                  path="interviews"
                  element={requireRole(RECRUITMENT_ROUTE_ROLES, RecruitmentInterviews)}
                />
                <Route path="candidates">
                  <Route
                    index
                    element={requireRole(RECRUITMENT_ROUTE_ROLES, RecruitmentCandidates)}
                  />
                  <Route
                    path="create"
                    element={requireRole(RECRUITMENT_ROUTE_ROLES, RecruitmentCandidateCreate)}
                  />
                </Route>
                <Route
                  path="reports"
                  element={requireRole(
                    [
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    RecruitmentReports
                  )}
                />
                <Route
                  path="careers"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    RecruitmentCareersSettings
                  )}
                />
              </Route>

              {/* Onboarding — role gates aligned with backend onboardingRouter + header access policy */}
              <Route
                path="/onboarding/"
                element={requireRole([Configs.securityLevelIC], OnboardingPlan)}
              />
              <Route
                path="/onboarding/hr"
                element={requireRole(
                  [
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                    Configs.securityLevelHRBP,
                  ],
                  OnboardingHR,
                  { hrbpModuleCode: HRBP_MODULE_CODE_ONBOARDING }
                )}
              />
              <Route
                path="/onboarding/templates"
                element={requireRole(
                  [
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                  ],
                  OnboardingTemplates
                )}
              />
              <Route
                path="/onboarding/plan/:encryptedParams"
                element={requireRole(
                  [
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                    Configs.securityLevelIC,
                    Configs.securityLevelHRBP,
                  ],
                  OnboardingPlan,
                  { hrbpModuleCode: HRBP_MODULE_CODE_ONBOARDING }
                )}
              />
              <Route
                path="/onboarding/plan/"
                element={requireRole(
                  [
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                    Configs.securityLevelIC,
                    Configs.securityLevelHRBP,
                  ],
                  OnboardingPlan,
                  { hrbpModuleCode: HRBP_MODULE_CODE_ONBOARDING }
                )}
              />
              <Route
                path="/onboarding/template"
                element={requireRole(
                  [
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                  ],
                  OnboardingTemplate
                )}
              />
              <Route
                path="/onboarding/template/:encryptedParams"
                element={requireRole(
                  [
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                  ],
                  OnboardingTemplate
                )}
              />
              <Route
                path="/onboarding/reporting"
                element={requireRole(
                  [
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                  ],
                  OnboardingReporting
                )}
              />

              <Route path="/career/">
                <Route index element={<Navigate to="self" replace />} />
                <Route path="self" element={requireRole([Configs.securityLevelIC], CareerHome)} />
                <Route path="team">
                  <Route
                    index
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                      ],
                      CareerTeam
                    )}
                  />
                  <Route
                    path=":encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                      ],
                      CareerHome
                    )}
                  />
                </Route>
                <Route path="hr">
                  <Route
                    index
                    element={requireRole(
                      [Configs.securityLevelHR, Configs.securityLevelExec],
                      CareerHR
                    )}
                  />
                  <Route path="guideline">
                    <Route
                      path=":encryptedParams"
                      element={requireRole(
                        [Configs.securityLevelHR, Configs.securityLevelExec],
                        CareerGuidelineCreate
                      )}
                    />
                    <Route
                      path="create"
                      element={requireRole(
                        [Configs.securityLevelHR, Configs.securityLevelExec],
                        CareerGuidelineCreate
                      )}
                    />
                    <Route
                      path="edit/:encryptedParams"
                      element={requireRole(
                        [Configs.securityLevelHR, Configs.securityLevelExec],
                        CareerGuidelineCreate
                      )}
                    />
                  </Route>
                </Route>
                <Route
                  path="reporting"
                  element={requireRole(
                    [
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    CareerReporting
                  )}
                />
              </Route>

              <Route path="/learning">
                <Route index element={<Navigate to="/learning/dashboard" replace />} />
                <Route path="dashboard/">
                  <Route index element={<LearningHome />} />
                  <Route path=":encryptedParams" element={<LearningDashboardTeamRedirect />} />
                </Route>
                <Route path="team/">
                  <Route index element={<LearningTeam />} />
                  <Route path=":encryptedParams" element={<LearningHomeTeamMember />} />
                </Route>
                <Route path="catalogue/">
                  <Route index element={<LearningCatalogue />} />
                  <Route path=":encryptedParams" element={<LearningCourseProfile />} />
                </Route>
              </Route>

              {/* PERFORM */}
              <Route path="/calibration">
                <Route index element={<Navigate to="/calibration/calibrate" replace />} />
                <Route
                  path="calibrate/"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    CalibrationHome,
                    { hrbpModuleCode: HRBP_MODULE_CODE_CALIBRATION }
                  )}
                />
                <Route
                  path="calibrate/:encryptedParams"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    CalibrationCalibrate,
                    { hrbpModuleCode: HRBP_MODULE_CODE_CALIBRATION }
                  )}
                />
                <Route
                  path="session/"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    CalibrationSession,
                    { hrbpModuleCode: HRBP_MODULE_CODE_CALIBRATION }
                  )}
                />
                <Route
                  path="campaigns/"
                  element={requireRole(
                    [
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    CalibrationCampaigns,
                    { hrbpModuleCode: HRBP_MODULE_CODE_CALIBRATION }
                  )}
                />
              </Route>

              <Route path="/performance">
                <Route index element={<Navigate to="/performance/self" replace />} />
                <Route path="self">
                  <Route index element={<PerformanceHome />} />
                  <Route path="peers" element={<PerformanceCampaignPeers />} />
                  <Route path="inputs" element={<PerformanceInput />} />
                  <Route path="strengths" element={<PerformanceInput />} />
                  <Route path="review" element={<PerformanceReview />} />
                  <Route path="discuss" element={<PerformanceDiscuss />} />
                  <Route path=":notificationID" element={<PerformanceHome />} />
                  <Route
                    path="campaign/:companyIDparam/:id/:employeeIDparam"
                    element={<PerformanceCampaignResult />}
                  />
                </Route>

                <Route path="feedback">
                  <Route index element={<PerformanceFeedbacks />} />
                  <Route
                    path=":peerEmployeeID/:peerCompanyID/:peerCampaignID/:selfEmail/:notificationID"
                    element={<PerformanceFeedback />}
                  />
                  <Route
                    path=":peerEmployeeID/:peerCompanyID/:peerCampaignID/:selfEmail/"
                    element={<PerformanceFeedback />}
                  />
                </Route>

                <Route path="team">
                  <Route
                    index
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      PerformanceTeam,
                      { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                    )}
                  />
                  <Route path="employee">
                    <Route
                      index
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceHome,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                    <Route
                      path="peers/:encryptedParams"
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceCampaignPeers,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                    <Route
                      path=":encryptedParams"
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceHome,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                    <Route
                      path="inputs/:encryptedParams"
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceInput,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                    <Route
                      path="strengths/:encryptedParams"
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceInput,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                    <Route
                      path="review/:encryptedParams"
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceReview,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                    <Route
                      path="discuss/:encryptedParams"
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceDiscuss,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                    <Route
                      path="campaign/:companyIDparam/:id/:employeeIDparam"
                      element={requireRole(
                        [
                          Configs.securityLevelManager,
                          Configs.securityLevelHR,
                          Configs.securityLevelExec,
                          Configs.securityLevelAdmin,
                          Configs.securityLevelHRBP,
                        ],
                        PerformanceCampaignResult,
                        { hrbpModuleCode: HRBP_MODULE_CODE_PERFORMANCE }
                      )}
                    />
                  </Route>
                </Route>

                <Route path="campaigns">
                  <Route
                    index
                    element={requireRole(
                      [
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                      ],
                      PerformanceCampaigns
                    )}
                  />
                  <Route
                    path="create"
                    element={requireRole(
                      [
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                      ],
                      PerformanceCampaign
                    )}
                  />
                  <Route
                    path="edit/:encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                      ],
                      PerformanceCampaign
                    )}
                  />
                </Route>
                <Route path="admin" element={<PerformanceAdmin />} />
                <Route
                  path="hr"
                  element={requireRole(
                    [
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    PerformanceHR
                  )}
                />
              </Route>

              <Route path="/goals/">
                <Route index element={<Navigate to="/goals/dashboard" replace />} />
                <Route
                  path="dashboard"
                  element={requireRole([Configs.securityLevelIC], GoalsHome)}
                />
                <Route
                  path="company"
                  element={requireRole([Configs.securityLevelIC], GoalsCompany)}
                />
                <Route path="team">
                  <Route
                    index
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                      ],
                      GoalsTeam
                    )}
                  />
                  <Route
                    path=":encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                      ],
                      GoalsTeamMember
                    )}
                  />
                </Route>
                <Route path="create" element={requireRole([Configs.securityLevelIC], GoalCreate)} />{' '}
                {/* Modal */}
                <Route
                  path="edit/:goalID"
                  element={requireRole([Configs.securityLevelIC], GoalCreate)}
                />{' '}
                {/* Modal */}
                <Route
                  path=":goalID"
                  element={requireRole([Configs.securityLevelIC], GoalProfile)}
                />
              </Route>

              <Route path="/development/">
                <Route index element={<Navigate to="/development/self" replace />} />
                <Route
                  path="dashboard"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    DevelopmentDashboard
                  )}
                />
                <Route
                  path="templates"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    DevelopmentTemplates
                  )}
                />
                <Route
                  path="template"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    DevelopmentPlanTemplate
                  )}
                />
                <Route
                  path="template/:templateId"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                    ],
                    DevelopmentPlanTemplate
                  )}
                />
                <Route path="self/">
                  <Route
                    index
                    element={requireRole([Configs.securityLevelIC], DevelopmentHomeSelf)}
                  />
                  <Route
                    path=":encryptedParams"
                    element={requireRole([Configs.securityLevelIC], DevelopmentPlanProfile)}
                  />
                </Route>
                <Route path="shared/">
                  <Route
                    index
                    element={requireRole([Configs.securityLevelIC], DevelopmentHomeShared)}
                  />
                  <Route
                    path=":encryptedParams"
                    element={requireRole([Configs.securityLevelIC], DevelopmentPlanProfile)}
                  />
                </Route>
                <Route path="team">
                  <Route
                    index
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      DevelopmentTeam,
                      { hrbpModuleCode: HRBP_MODULE_CODE_DEVELOPMENT }
                    )}
                  />
                  <Route
                    path=":encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      DevelopmentHomeTeamMember,
                      { hrbpModuleCode: HRBP_MODULE_CODE_DEVELOPMENT }
                    )}
                  />
                  <Route
                    path="plan/:encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      DevelopmentPlanProfile,
                      { hrbpModuleCode: HRBP_MODULE_CODE_DEVELOPMENT }
                    )}
                  />
                  <Route
                    path="plan/create/:encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      DevelopmentPlanCreate,
                      { hrbpModuleCode: HRBP_MODULE_CODE_DEVELOPMENT }
                    )}
                  />
                  <Route
                    path="plan/edit/:encryptedParams"
                    element={requireRole(
                      [
                        Configs.securityLevelManager,
                        Configs.securityLevelHR,
                        Configs.securityLevelExec,
                        Configs.securityLevelAdmin,
                        Configs.securityLevelHRBP,
                      ],
                      DevelopmentPlanCreate,
                      { hrbpModuleCode: HRBP_MODULE_CODE_DEVELOPMENT }
                    )}
                  />
                </Route>
                {/* Move create routes after team routes to avoid route matching conflicts */}
                <Route
                  path="create/:encryptedParams"
                  element={requireRole(
                    [
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    DevelopmentPlanCreate
                  )}
                />
                <Route
                  path="create"
                  element={requireRole(
                    [
                      Configs.securityLevelIC,
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    DevelopmentPlanCreate
                  )}
                />
                <Route
                  path="edit/:planID"
                  element={requireRole(
                    [
                      Configs.securityLevelIC,
                      Configs.securityLevelManager,
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                    ],
                    DevelopmentPlanCreate
                  )}
                />
                <Route
                  path="reporting"
                  element={<Navigate to="/development/dashboard" replace />}
                />
                <Route path="settings" element={<Navigate to="/development/dashboard" replace />} />
              </Route>

              {/* ESSENTIALS */}
              <Route path="/inbox/">
                <Route index element={<Navigate to="self" replace />} />
                <Route path="self" element={requireRole([Configs.securityLevelIC], HRInbox)} />
                <Route
                  path="hr"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    HRDashboard
                  )}
                />
                <Route
                  path="conversation/:encryptedParams"
                  element={requireRole([Configs.securityLevelIC], HRConversation)}
                />
              </Route>

              {/******* days off ********/}
              <Route
                path={DAYS_OFF_CONFIG.routing.self.path}
                element={requireRole(DAYS_OFF_CONFIG.routing.self.securityLevel, DaysOffHome)}
              />
              <Route
                path={DAYS_OFF_CONFIG.routing.team.path}
                element={requireRole(DAYS_OFF_CONFIG.routing.team.securityLevel, DaysOffTeam)}
              />
              <Route
                path={DAYS_OFF_CONFIG.routing.award.path}
                element={requireRole(DAYS_OFF_CONFIG.routing.award.securityLevel, DaysOffAward)}
              />
              <Route
                path={DAYS_OFF_CONFIG.routing.teamWithParams.path}
                element={requireRole(
                  DAYS_OFF_CONFIG.routing.teamWithParams.securityLevel,
                  DaysOffHome
                )}
              />
              <Route
                path={DAYS_OFF_CONFIG.routing.teamDetailed.path}
                element={requireRole(
                  DAYS_OFF_CONFIG.routing.teamDetailed.securityLevel,
                  DaysOffTeamCalendar
                )}
              />
              <Route
                path={DAYS_OFF_CONFIG.routing.hr.path}
                element={requireRole(DAYS_OFF_CONFIG.routing.hr.securityLevel, DaysOffHR)}
              />
              <Route
                path={DAYS_OFF_CONFIG.routing.request.path}
                element={requireRole(DAYS_OFF_CONFIG.routing.request.securityLevel, DaysOffRequest)}
              />
              <Route
                path={DAYS_OFF_CONFIG.routing.requestWithParams.path}
                element={requireRole(
                  DAYS_OFF_CONFIG.routing.requestWithParams.securityLevel,
                  DaysOffRequest
                )}
              />

              {/******* PUNCH ********/}
              {/* Legacy /punch-v2/* bookmarks, emails and MCP links → canonical /punch/* paths */}
              <Route
                path="/punch-v2/punch"
                element={<Navigate to={PUNCH_V2_CONFIG.routing.self.path} replace />}
              />
              <Route
                path="/punch-v2/punch/"
                element={<Navigate to={PUNCH_V2_CONFIG.routing.self.path} replace />}
              />
              <Route
                path="/punch-v2/myteam"
                element={<Navigate to={PUNCH_V2_CONFIG.routing.hr.path} replace />}
              />
              <Route
                path="/punch-v2/myteam/"
                element={<Navigate to={PUNCH_V2_CONFIG.routing.hr.path} replace />}
              />
              <Route
                path="/punch-v2/projects"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.projects} replace />}
              />
              <Route
                path="/punch-v2/projects/"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.projects} replace />}
              />
              <Route
                path="/punch-v2/contracts"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.contracts} replace />}
              />
              <Route
                path="/punch-v2/contracts/"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.contracts} replace />}
              />
              <Route
                path="/punch-v2/agreements"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.agreements} replace />}
              />
              <Route
                path="/punch-v2/agreements/"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.agreements} replace />}
              />
              <Route
                path="/punch-v2/settings"
                element={
                  <Navigate
                    to={`${EMPLOYEE_SETTINGS_CONFIG.routing.basePath}/agreements`}
                    replace
                  />
                }
              />
              <Route
                path="/punch-v2/settings/"
                element={<Navigate to={SETTINGS_ROUTES.company.punch} replace />}
              />
              <Route
                path="/punch-v2/settings/agreements"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.agreements} replace />}
              />
              <Route
                path="/punch-v2/settings/contracts"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.contracts} replace />}
              />
              <Route
                path="/punch-v2/settings/projects"
                element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.projects} replace />}
              />
              <Route
                path="/punch-v2/settings/timesheet"
                element={<Navigate to={SETTINGS_ROUTES.company.punch} replace />}
              />
              <Route element={<PunchV2Provider />}>
                <Route
                  path={PUNCH_V2_CONFIG.routing.self.path}
                  element={requireRole(PUNCH_V2_CONFIG.routing.self.securityLevel, PunchV2Home)}
                />
                <Route
                  path={PUNCH_V2_CONFIG.routing.hr.path}
                  element={requireRole(PUNCH_V2_CONFIG.routing.hr.securityLevel, PunchV2HR)}
                />
              </Route>

              {/******* ADMIN (Super admin) ********/}
              <Route
                path="/admin"
                element={<Navigate to={ADMIN_CONFIG.routing.companies.path} replace />}
              />
              <Route
                path="/admin/"
                element={<Navigate to={ADMIN_CONFIG.routing.companies.path} replace />}
              />
              <Route
                path={AGENTS_ADMIN_CONFIG.routing.root.path}
                element={
                  <Navigate to={AGENTS_ADMIN_CONFIG.routing.roi.path} replace />
                }
              />
              <Route
                path={AGENTS_ADMIN_CONFIG.routing.roi.path}
                element={requireRole(
                  AGENTS_ADMIN_CONFIG.routing.roi.securityLevel,
                  AgentsRoiPage
                )}
              />
              <Route
                path={AGENTS_ADMIN_CONFIG.routing.prompts.path}
                element={requireRole(
                  AGENTS_ADMIN_CONFIG.routing.prompts.securityLevel,
                  AgentsPromptLibraryPage
                )}
              />
              <Route
                path={ADMIN_CONFIG.routing.companyCreate.path}
                element={requireRole(
                  ADMIN_CONFIG.routing.companyCreate.securityLevel,
                  AdminCompanyCreate
                )}
              />
              <Route
                path={ADMIN_CONFIG.routing.companies.path}
                element={requireRole(
                  ADMIN_CONFIG.routing.companies.securityLevel,
                  AdminCompaniesList
                )}
              />
              <Route
                path={ADMIN_CONFIG.routing.companyDetail.path}
                element={requireRole(
                  ADMIN_CONFIG.routing.companyDetail.securityLevel,
                  AdminCompanyDetail
                )}
              />
              <Route
                path={ADMIN_CONFIG.routing.campaignCreate.path}
                element={requireRole(
                  ADMIN_CONFIG.routing.campaignCreate.securityLevel,
                  AdminCampaignCreate
                )}
              />
              <Route
                path={ADMIN_CONFIG.routing.campaigns.path}
                element={requireRole(
                  ADMIN_CONFIG.routing.campaigns.securityLevel,
                  AdminCampaignsList
                )}
              />
              <Route
                path={ADMIN_CONFIG.routing.campaignDetail.path}
                element={requireRole(
                  ADMIN_CONFIG.routing.campaignDetail.securityLevel,
                  AdminCampaignDetail
                )}
              />
              {/* Legacy Admin V2 path redirects */}
              <Route
                path="/adminv2"
                element={<Navigate to={ADMIN_CONFIG.routing.companies.path} replace />}
              />
              <Route
                path="/adminv2/companies"
                element={<Navigate to={ADMIN_CONFIG.routing.companies.path} replace />}
              />
              <Route
                path="/adminv2/companies/create"
                element={<Navigate to={ADMIN_CONFIG.routing.companyCreate.path} replace />}
              />
              <Route
                path="/adminv2/companies/:companyId"
                element={<AdminV2CompanyDetailRedirect />}
              />
              <Route
                path="/adminv2/campaigns"
                element={<Navigate to={ADMIN_CONFIG.routing.campaigns.path} replace />}
              />
              <Route
                path="/adminv2/campaigns/create"
                element={<Navigate to={ADMIN_CONFIG.routing.campaignCreate.path} replace />}
              />
              <Route
                path="/adminv2/campaigns/:campaignId"
                element={<AdminV2CampaignDetailRedirect />}
              />

              <Route path="/profile">
                <Route index element={requireRole([Configs.securityLevelIC], Profile)} />
                <Route
                  path=":encryptedParams"
                  element={requireRole([Configs.securityLevelIC], Profile)}
                />
                <Route path="job" element={requireRole([Configs.securityLevelIC], ProfileJob)} />
                <Route path="comp" element={requireRole([Configs.securityLevelIC], ProfileComp)} />
                <Route
                  path="documents"
                  element={requireRole(
                    [Configs.securityLevelIC],
                    ProfileDocumentsRoute
                  )}
                />
              </Route>

              <Route path="/organigram">
                <Route index element={<Navigate to="/organigram/home" replace />} />
                <Route path="home" element={requireRole([Configs.securityLevelIC], Organigram)} />
                <Route
                  path="employees"
                  element={requireRole([Configs.securityLevelIC], OrganigramEmployeeListRoute)}
                />
                <Route
                  path="employees/create"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    OrganigramEmployeeCreate
                  )}
                />
                <Route
                  path="employees/edit/:encryptedParams"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    OrganigramEmployeeCreate
                  )}
                />
                <Route
                  path="reorganize"
                  element={requireRole(
                    [
                      Configs.securityLevelHR,
                      Configs.securityLevelExec,
                      Configs.securityLevelAdmin,
                      Configs.securityLevelHRBP,
                    ],
                    OrganigramReOrganize
                  )}
                />
                <Route
                  path="employees/roles/:employeeID"
                  element={requireRole(
                    [Configs.securityLevelAdmin],
                    OrganigramRolesAssignmentPage
                  )}
                />
                <Route
                  path="dataModel"
                  element={requireRole(
                    [Configs.securityLevelHR, Configs.securityLevelExec],
                    OrganigramDataModel
                  )}
                />
              </Route>

              <Route path="/settings">
                <Route index element={<SettingsIndexRedirect />} />
                <Route
                  path="language"
                  element={<Navigate to={SETTINGS_ROUTES.personal} replace />}
                />
                <Route
                  path="personal"
                  element={requireRole(SETTINGS_PERSONAL_SECURITY_LEVELS, SettingsPersonal)}
                />
                <Route path="company">
                  <Route
                    index
                    element={requireRole(SETTINGS_COMPANY_SECURITY_LEVELS, SettingsCompanyHub)}
                  />
                  <Route
                    path="personalization"
                    element={requireRole(
                      SETTINGS_COMPANY_SECURITY_LEVELS,
                      SettingsCompanyPersonalization
                    )}
                  />
                  <Route
                    path="integrations"
                    element={requireRole(
                      SETTINGS_COMPANY_SECURITY_LEVELS,
                      SettingsCompanyIntegrations
                    )}
                  />
                  <Route
                    path="billing"
                    element={requireRole(SETTINGS_COMPANY_SECURITY_LEVELS, SettingsCompanyBilling)}
                  />
                  <Route
                    path="pulse"
                    element={requireRole(SETTINGS_COMPANY_SECURITY_LEVELS, SettingsCompanyPulse)}
                  />
                  <Route
                    path="calibration"
                    element={requireRole(
                      SETTINGS_COMPANY_SECURITY_LEVELS,
                      SettingsCompanyCalibration
                    )}
                  />
                  <Route
                    path="recognition"
                    element={requireRole(
                      SETTINGS_COMPANY_SECURITY_LEVELS,
                      SettingsCompanyRecognition
                    )}
                  />
                  <Route
                    path="goals"
                    element={requireRole(
                      SETTINGS_COMPANY_SECURITY_LEVELS,
                      SettingsCompanyGoals
                    )}
                  />
                  <Route
                    path="punch"
                    element={requireRole(SETTINGS_COMPANY_SECURITY_LEVELS, SettingsCompanyPunch)}
                  />
                  <Route
                    path="compliance"
                    element={requireRole(
                      SETTINGS_COMPANY_SECURITY_LEVELS,
                      SettingsCompanyCompliance
                    )}
                  />
                  <Route
                    path="modules/:moduleSlug"
                    element={requireRole(
                      SETTINGS_COMPANY_SECURITY_LEVELS,
                      SettingsCompanyModuleOverview
                    )}
                  />
                  <Route
                    path="kai"
                    element={requireRole(SETTINGS_COMPANY_SECURITY_LEVELS, SettingsKAI)}
                  />
                  <Route
                    path="hr-pocs"
                    element={requireRole(SETTINGS_COMPANY_SECURITY_LEVELS, SettingsCompanyHrPocs)}
                  />
                  <Route
                    path="configure"
                    element={<Navigate to={SETTINGS_ROUTES.company.index} replace />}
                  />
                </Route>
                <Route path="kai" element={<Navigate to={SETTINGS_ROUTES.company.kai} replace />} />
                <Route
                  path="timesheet"
                  element={
                    <Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.agreements} replace />
                  }
                />
                <Route
                  path="timesheet/"
                  element={
                    <Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.agreements} replace />
                  }
                />
                <Route
                  path="timesheet/agreements"
                  element={
                    <Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.agreements} replace />
                  }
                />
                <Route
                  path="timesheet/contracts"
                  element={
                    <Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.contracts} replace />
                  }
                />
                <Route
                  path="timesheet/projects"
                  element={<Navigate to={EMPLOYEE_SETTINGS_CONFIG.routing.tabs.projects} replace />}
                />
                <Route
                  path="timesheet/options"
                  element={<Navigate to={SETTINGS_ROUTES.company.punch} replace />}
                />
                <Route
                  path="employee-settings"
                  element={requireRole(
                    EMPLOYEE_SETTINGS_CONFIG.routing.securityLevel,
                    EmployeeSettingsHub
                  )}
                >
                  <Route index element={<Navigate to="agreements" replace />} />
                  <Route path="agreements" element={<EmployeeSettingsTabAgreements />} />
                  <Route path="contracts" element={<EmployeeSettingsTabContracts />} />
                  <Route path="pending" element={<EmployeeSettingsTabPending />} />
                  <Route
                    path="employees-without-contract"
                    element={
                      <Navigate
                        to={SETTINGS_ROUTES.employeeSettings.tabs.pendingContract}
                        replace
                      />
                    }
                  />
                  <Route path="projects" element={<EmployeeSettingsTabProjects />} />
                  <Route
                    path="employees-without-project"
                    element={
                      <Navigate to={SETTINGS_ROUTES.employeeSettings.tabs.pendingProject} replace />
                    }
                  />
                  <Route
                    path="options"
                    element={<Navigate to={SETTINGS_ROUTES.company.punch} replace />}
                  />
                </Route>
              </Route>

              {/* Culture */}
              <Route
                path="/culture/"
                element={requireRole(
                  [Configs.securityLevelHR, Configs.securityLevelExec],
                  CultureHome
                )}
              />
              <Route
                path="/culture/values"
                element={requireRole(
                  [Configs.securityLevelHR, Configs.securityLevelExec],
                  CultureValues
                )}
              />
              <Route
                path="/culture/behaviours"
                element={requireRole(
                  [Configs.securityLevelHR, Configs.securityLevelExec],
                  CultureBehaviours
                )}
              />
              <Route
                path="/culture/drivers"
                element={requireRole(
                  [Configs.securityLevelHR, Configs.securityLevelExec],
                  CultureDrivers
                )}
              />
              <Route
                path="/culture/performance"
                element={requireRole(
                  [Configs.securityLevelHR, Configs.securityLevelExec],
                  CulturePerformance
                )}
              />
              <Route
                path="/culture/calendar"
                element={requireRole(
                  [Configs.securityLevelHR, Configs.securityLevelExec],
                  CultureCalendar
                )}
              />

            </Route>
            {/* No module header Layout */}
            <Route element={<LayoutHeaderless />}>
              {/* Externally accessible pages - no auth required  */}
              <Route path="/pulseSurvey" element={<PulseSurvey />} />
              <Route path="/pulseSurvey/" element={<PulseSurvey />} />
              <Route
                path="/pulseSurvey/:campaignID"
                element={requireRole(
                  [
                    Configs.securityLevelIC,
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelHRBP,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                  ],
                  AuthenticatedPulseSurvey
                )}
              />
              <Route
                path="/public/recognition/:id"
                element={<RecognitionPublicSingleRecognition />}
              />
              {/* <Route path="/pulseDaily/:encryptedParams" element={<PulseDaily/>} /> */}
              <Route path="/pulseDaily/" element={<PulseDaily />} />

              {/* Not found */}
              <Route path="/unauthorized" element={<Forbidden />} />
              <Route path="*" element={<Navigate to="/unauthorized" />} />
            </Route>
          </Routes>

          {/* Open Modal if state.background is defined */}
          {background && (
            <Routes>
              {/* ENGAGE */}
              {/* Goals */}
              <Route path="/goals/create" element={<GoalCreate />} />
              <Route path="/goals/edit/:goalID" element={<GoalCreate />} />

              {/* PERFORM */}
              {/* Calibration */}
              <Route
                path="/calibration/calibrate/:encryptedParams"
                element={requireRole(
                  [
                    Configs.securityLevelManager,
                    Configs.securityLevelHR,
                    Configs.securityLevelExec,
                    Configs.securityLevelAdmin,
                  ],
                  CalibrationCalibrate
                )}
              />

              {/* ESSENTIALS */}
              {/* Organigram */}
              <Route path="/organigram/employees/create" element={<OrganigramEmployeeCreate />} />
              <Route
                path="/organigram/employees/edit/:encryptedParams"
                element={<OrganigramEmployeeCreate />}
              />
              {/* DaysOff — award remains modal overlay; request is a full page */}
              <Route path={DAYS_OFF_CONFIG.routing.award.path} element={<DaysOffAward />} />
              {/* HR Inbox */}
              <Route path="/inbox/create" element={<HRNewRequest />} />
            </Routes>
          )}
        </ErrorBoundary>
      </Suspense>
    </React.Fragment>
  );
}

export default Routing;
